import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Box, Button, HStack, Icon, Text, VStack} from '@chakra-ui/react';
import {CheckIcon} from '@chakra-ui/icons';
import {useMemo, useState} from 'react';
import {ModaleLayout} from '@/components/Modale/ModaleLayout';
import {useTranslation} from 'react-i18next';
import {TrashIcon} from '@/components/svg/TrashIcon';
import {MotifRetraitCadencier} from 'api-types/ffconnect';

interface RetirerDuCadencierProps {
  validateAction: (raison: MotifRetraitCadencier) => void;
  defaultRaison?: MotifRetraitCadencier;
}

export const ModaleRetirerDuCadencier =
  NiceModal.create<RetirerDuCadencierProps>(
    ({validateAction, defaultRaison}) => {
      const modal = useModal();
      const {t} = useTranslation();

      const options: {key: MotifRetraitCadencier; label: string}[] = useMemo(
        () => [
          {
            key: 'plus_de_commande',
            label: t('cadencier.modale_retirer.raison_plus_de_commande'),
          },
          {
            key: 'pas_interesse',
            label: t('cadencier.modale_retirer.raison_pas_interesse'),
          },
          {
            key: 'autre_similaire',
            label: t('cadencier.modale_retirer.raison_autre_similaire'),
          },
          {
            key: 'deja_ailleurs',
            label: t('cadencier.modale_retirer.raison_deja_ailleurs'),
          },
        ],
        [t],
      );

      const [selected, setSelected] = useState<MotifRetraitCadencier>(
        defaultRaison ?? options[0].key,
      );

      const handleValidate = () => {
        validateAction(selected);
        modal.remove();
      };

      return (
        <ModaleLayout
          visible={modal.visible}
          hide={modal.remove}
          titre={t('cadencier.modale_retirer.titre')}
          contentStyle={{width: '100%'}}
        >
          <VStack
            alignItems='stretch'
            spacing={4}
          >
            <Text
              fontWeight='600'
              color='gray.dark'
            >
              {t('cadencier.modale_retirer.selectionner_raison')}
            </Text>

            <VStack
              spacing={3}
              alignItems='stretch'
            >
              {options.map((option) => (
                <OptionCard
                  key={option.key}
                  label={option.label}
                  selected={selected === option.key}
                  onClick={() => {
                    setSelected(option.key);
                  }}
                />
              ))}
            </VStack>

            <VStack
              spacing={3}
              alignItems='stretch'
              mt={4}
            >
              <Button
                leftIcon={<TrashIcon />}
                fontSize={{base: 'md', md: 'sm'}}
                bg='red.fail'
                color='white'
                px='8'
                h='52px'
                fontWeight='600'
                _hover={{bg: 'red.700'}}
                onClick={handleValidate}
              >
                {t('cadencier.modale_retirer.valider_et_retirer')}
              </Button>

              <Button
                fontSize='sm'
                bg='gray.light'
                color='black.text'
                px='8'
                h='52px'
                fontWeight='600'
                _hover={{bg: 'gray.400', borderColor: 'white'}}
                onClick={() => {
                  modal.remove();
                }}
              >
                {t('cadencier.modale_retirer.annuler')}
              </Button>
            </VStack>
          </VStack>
        </ModaleLayout>
      );
    },
  );

const OptionCard: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({label, selected, onClick}) => {
  return (
    <Box
      border='1px solid'
      borderColor={selected ? 'yellow.selected_text' : 'gray.light'}
      bg={selected ? 'yellow.selected_bg' : 'white'}
      color={'red'}
      borderRadius='12px'
      p={4}
      cursor='pointer'
      onClick={onClick}
    >
      <HStack
        alignItems='center'
        spacing={4}
      >
        <Box
          w='20px'
          h='20px'
          borderRadius='full'
          display='flex'
          alignItems='center'
          justifyContent='center'
          bg={selected ? 'yellow.selected_text' : 'transparent'}
          border={selected ? 'none' : '2px solid'}
          borderColor='gray.300'
        >
          {selected && (
            <Icon
              as={CheckIcon}
              color='white'
              boxSize={3}
            />
          )}
        </Box>
        <Text
          fontWeight={selected ? '600' : '400'}
          color={selected ? 'yellow.selected_text' : 'black.text'}
        >
          {label}
        </Text>
      </HStack>
    </Box>
  );
};

export default ModaleRetirerDuCadencier;
